import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ParticularService from '../services/ParticularService';
import particularesApiService from '../services/ParticularesApiService';
import useMensajesNoLeidos from '../hooks/useMensajesNoLeidos';
import { AulaOverview, AulaSection } from './common/AulaShell';
import StudentShell from './common/StudentShell';
import { buildParticularPreceptorMenuItems } from './common/menus/ParticularPreceptorMenu';
import EstadoAcademicoPanel from './evaluaciones/EstadoAcademicoPanel';
import EstadoAcademicoResumen from './evaluaciones/EstadoAcademicoResumen';
import AsistenciasDivision from './asistencias/AsistenciasDivision';
import EvaluacionesCursoModal from './evaluaciones/EvaluacionesCursoModal';
import { crearEvaluacionesApiParticular } from '../services/EvaluacionesApi';
import { isParticularModoColegio } from '../utils/particularChatGPTAccess';
import './NewDashboard.css';

// Panel básico del preceptor de una cuenta particular: saludo, acceso a mensajes y aviso de próximas funciones.
const getName = (item, fallback) => item?.nombre || item?.Nombre || fallback;

const ParticularPreceptorPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const activeMenu = ['academico', 'asistencias'].includes(location.state?.activeMenu) ? location.state.activeMenu : 'panel';
  // Curso abierto en la matriz de notas (solo lectura).
  const [cursoNotas, setCursoNotas] = useState(null);
  const cursoNotasApi = useMemo(
    () => (cursoNotas ? crearEvaluacionesApiParticular({ idCurso: cursoNotas.id }) : null),
    [cursoNotas]
  );
  const [particular, setParticular] = useState(() => ParticularService.getActiveParticular());
  const [error, setError] = useState('');
  const { count: mensajesNoLeidos } = useMensajesNoLeidos(true, { scope: 'particular' });

  useEffect(() => {
    let active = true;
    particularesApiService.getContexto()
      .then((response) => {
        if (active) setParticular(response?.particular || response?.Particular || ParticularService.getActiveParticular());
      })
      .catch((err) => active && setError(err?.message || 'No pudimos cargar la cuenta.'));
    return () => {
      active = false;
    };
  }, []);

  const particularName = getName(particular, 'Particular');
  const userName = user?.displayName || user?.name || 'Preceptor';
  const firstName = String(user?.nombre || userName).trim().split(/\s+/)[0];

  const handleNavigate = (item) => {
    if (item?.path) navigate(item.path, item.state ? { state: item.state } : undefined);
  };

  const shortcuts = [
    ...(isParticularModoColegio(particular) ? [{
      key: 'asistencias',
      label: 'Asistencias',
      icon: 'fa-clipboard-check',
      path: '/particular/preceptor',
      state: { activeMenu: 'asistencias' },
      description: 'Tomá asistencia a tus divisiones: generá el código y seguí quién dio el presente.'
    }] : []),
    {
      key: 'academico',
      label: 'Estado académico',
      icon: 'fa-chart-bar',
      path: '/particular/preceptor',
      state: { activeMenu: 'academico' },
      description: isParticularModoColegio(particular)
        ? 'Situación de los alumnos de tus divisiones, por curso y por alumno.'
        : 'Situación de los alumnos de tus cursos, por curso y por alumno.'
    },
    {
      key: 'mensajes',
      label: 'Mensajes',
      icon: 'fa-envelope',
      path: '/mensajes',
      state: { scope: 'particulares' },
      description: 'Revisá tu bandeja y escribile al equipo docente de la cuenta.',
      count: mensajesNoLeidos
    }
  ];

  return (
    <StudentShell
      activeId={activeMenu}
      onNavigate={handleNavigate}
      user={user}
      onLogout={logout}
      particularName={particularName}
      buildMenuItems={buildParticularPreceptorMenuItems}
      userFallbackLabel="Preceptor"
    >
      {activeMenu === 'asistencias' ? (
        isParticularModoColegio(particular)
          ? <AsistenciasDivision />
          : <AulaSection active icon="fa-clipboard-check" title="Asistencias" subtitle="Disponible cuando la cuenta tiene habilitado el Modo colegio." />
      ) : activeMenu === 'academico' ? (
        <EstadoAcademicoPanel
          subtitulo={isParticularModoColegio(particular)
            ? 'Alumnos de las divisiones que tenés a cargo.'
            : 'Alumnos de los cursos que tenés a cargo.'}
          cuentaNombre={particularName}
          onVerCurso={(curso) => setCursoNotas(curso)}
        />
      ) : (
      <>
      <div className="aula-home">
        <section className="aula-hero">
          <span className="aula-hero-badge">
            <i className="fas fa-user-shield" aria-hidden="true" />
            Preceptor
          </span>
          <h1>Hola, {firstName}</h1>
          <p className="aula-hero-desc">
            Este es tu espacio en {particularName}. Desde acá podés seguir tus mensajes con el equipo docente.
          </p>
          <dl className="aula-hero-meta">
            <div>
              <dt>Mensajes sin leer</dt>
              <dd>{mensajesNoLeidos || 0}</dd>
            </div>
          </dl>
        </section>
        <AulaOverview
          items={shortcuts}
          onSelect={(key) => handleNavigate(shortcuts.find((item) => item.key === key))}
        />
      </div>

      {error && <p className="sp-cursos-error" role="alert">{error}</p>}
      {/* Situación real de sus alumnos: notas, asistencia y alertas. */}
      <EstadoAcademicoResumen
        titulo={isParticularModoColegio(particular) ? 'Situación de tus divisiones' : 'Situación de tus alumnos'}
        onVerTodo={() => handleNavigate({ path: '/particular/preceptor', state: { activeMenu: 'academico' } })}
      />
      </>
      )}

      {cursoNotas && cursoNotasApi && (
        <EvaluacionesCursoModal
          api={cursoNotasApi}
          cursoNombre={cursoNotas.nombre}
          soloResumen
          onClose={() => setCursoNotas(null)}
        />
      )}
    </StudentShell>
  );
};

export default ParticularPreceptorPanel;
